import {
    ChangeDetectionStrategy, Component,
    EventEmitter, Input, OnChanges, Output,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { GridsterConfig, GridsterItem } from "angular-gridster2";
import { Dialog } from "@core/material";
import { GanttAxisStart, GanttCompiledData, GanttComponentData, GanttViewMode, GANTT_OPTION_DEFAULT, ReportComponentType, ResolvedTypography } from "@core/data/types/advanced-report";
import { editModeCheck } from "../../report-view/report-state/advanced-report.state";
import { ReportGanttEditFormDialog, ReportGanttEditFormDialogData } from "../../report-dialogs/report-gantt-edit-form/report-gantt-edit-form.dialog";

/** Orchestrates the Gantt report item (menu, layering, chrome); rendering is delegated to `pv-report-gantt-graph`. */
@Component({
    selector: 'pv-report-gantt',
    templateUrl: 'report-gantt-component.html',
    styleUrls:  ['report-gantt-component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
})
export class ReportGanttComponent implements OnChanges {

    @Input() ganttControl:   FormControl;
    @Input() typography:     ResolvedTypography;
    @Input() compiledData:   GanttCompiledData;
    @Input() configFormGroup: FormGroup;
    @Input() item:           GridsterItem;
    @Input() editMode:       boolean;
    @Input() optionKey:      string;
    @Input() reportKey:      string;
    @Input() ownerOrgKey:    string;
    @Input() gridsterConfig: GridsterConfig;

    @Output() delete    = new EventEmitter<boolean>();
    @Output() duplicate = new EventEmitter<ReportComponentType>();
    @Output() editTypography = new EventEmitter<void>();

    layerIndex = 1;

    get isUnset(): boolean {
        const v = this.ganttControl?.value as GanttComponentData;
        return !v || !v.charIds || v.charIds.length === 0;
    }

    /** Configured but no plottable rows. Stays false while `compiledData` is null (loading). */
    get hasNoData(): boolean {
        if (this.isUnset || !this.compiledData) return false;
        const datasets = this.compiledData.datasets ?? [];
        return !datasets.some(ds => (ds.data?.length ?? 0) > 0);
    }

    constructor(private _dialog: Dialog) {}

    ngOnChanges() {
        this.layerIndex = this.item && this.item.layerIndex ? this.item.layerIndex : 1;
    }

    moveToTop() {
        if (!editModeCheck(this.editMode)) return;
        this.configFormGroup.patchValue({ layerIndex: this.gridsterConfig.maxLayerIndex });
    }
    moveForward() {
        if (!editModeCheck(this.editMode)) return;
        this.configFormGroup.patchValue({ layerIndex: this.gridsterConfig.baseLayerIndex });
    }
    moveDown() {
        if (!editModeCheck(this.editMode)) return;
        this.configFormGroup.patchValue({ layerIndex: -this.gridsterConfig.baseLayerIndex });
    }
    moveToBottom() {
        if (!editModeCheck(this.editMode)) return;
        this.configFormGroup.patchValue({ layerIndex: -this.gridsterConfig.maxLayerIndex });
    }

    edit() {
        if (!editModeCheck(this.editMode)) return;
        const dialogData: ReportGanttEditFormDialogData = {
            reportKey:   this.reportKey,
            ownerOrgKey: this.ownerOrgKey,
            optionKey:   this.optionKey,
            data:        this.ganttControl.value,
        };
        this._dialog.open(ReportGanttEditFormDialog, {
            data: dialogData,
            panelClass: 'reporting-theme',
            width: '720px',
            maxWidth: '92vw',
            maxHeight: '90vh',
            autoFocus: false,
        }).afterClosed().subscribe(result => {
            if (!result?.status) return;
            this.ganttControl.setValue(result.data);
            this.ganttControl.markAsDirty();
        });
    }

    remove() {
        this._dialog.confirm('Delete Component', 'Delete this Gantt chart?', 'Delete', 'Cancel', true)
            .afterClosed().subscribe(r => { if (r) this.delete.emit(true); });
    }

    makeDuplicate() { this.duplicate.emit(ReportComponentType.GANTT); }

    get viewMode(): GanttViewMode {
        return ((this.ganttControl?.value as GanttComponentData)?.viewMode) ?? 'zoomed';
    }

    toggleViewMode() {
        if (!editModeCheck(this.editMode)) return;
        const cfg = (this.ganttControl?.value ?? GANTT_OPTION_DEFAULT) as GanttComponentData;
        const next: GanttViewMode = this.viewMode === 'full' ? 'zoomed' : 'full';
        this.ganttControl.setValue({ ...cfg, viewMode: next });
        this.ganttControl.markAsDirty();
    }

    get axisStartWeek(): GanttAxisStart {
        return ((this.ganttControl?.value as GanttComponentData)?.axisStartWeek) ?? 1;
    }

    /** Toggle calendar/split-year axis. Changing the control triggers a recompile, so the server re-rotates. */
    toggleAxisStart() {
        if (!editModeCheck(this.editMode)) return;
        const cfg = (this.ganttControl?.value ?? GANTT_OPTION_DEFAULT) as GanttComponentData;
        const next: GanttAxisStart = this.axisStartWeek === 27 ? 1 : 27;
        this.ganttControl.setValue({ ...cfg, axisStartWeek: next });
        this.ganttControl.markAsDirty();
    }
}
